/**
 * LogLevelDataAccessPage — admin audit trail viewer.
 *
 * GET /api/v1/log-level-data-access returns recent access-audit rows plus the
 * facet lists (users, resource_types, actions) that drive the filter bar.
 *
 * UI: filter bar (user, resource type, action, date range) + CSV export on
 * the right, dense monospace table with color pills for the result column.
 * Emerald = allow, amber = warn, red = deny.
 */
import { useMemo, useState } from "react";
import {
	useLogLevelDataAccessExport,
	useLogLevelDataAccessList,
} from "./hooks";
import type { AuditLogFilters, AuditLogRow, AuditResult } from "./api";

const CARD = "rounded-lg border border-zinc-200 bg-white shadow-sm";
const TH =
	"px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-zinc-500";
const TD = "px-3 py-2 text-xs text-zinc-900";
const SELECT =
	"rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-xs text-zinc-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500";
const INPUT =
	"rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-xs text-zinc-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500";
const BTN_PRIMARY =
	"inline-flex items-center gap-1.5 rounded-md border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 shadow-sm hover:bg-zinc-50";

function formatTs(ts: number | null | undefined): string {
	if (!ts) return "—";
	const ms = ts > 10_000_000_000 ? ts : ts * 1000;
	return new Date(ms).toLocaleString("en-US", {
		month: "short",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
		hour12: false,
	});
}

function resultPill(r: AuditResult | string) {
	const base =
		"inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium leading-none";
	if (r === "allow")
		return (
			<span className={`${base} bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200`}>
				allow
			</span>
		);
	if (r === "warn")
		return (
			<span className={`${base} bg-amber-50 text-amber-700 ring-1 ring-amber-200`}>warn</span>
		);
	if (r === "deny")
		return (
			<span className={`${base} bg-red-50 text-red-700 ring-1 ring-red-200`}>deny</span>
		);
	return <span className={`${base} bg-zinc-50 text-zinc-600 ring-1 ring-zinc-200`}>{r}</span>;
}

function fromIsoDate(iso: string, endOfDay: boolean): number | undefined {
	if (!iso) return undefined;
	const ms = Date.parse(endOfDay ? `${iso}T23:59:59Z` : `${iso}T00:00:00Z`);
	if (Number.isNaN(ms)) return undefined;
	return Math.floor(ms / 1000);
}

function downloadCsv(csv: string, filename: string) {
	const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();
	URL.revokeObjectURL(url);
}

export function LogLevelDataAccessPage() {
	const [userId, setUserId] = useState<string>("");
	const [resourceType, setResourceType] = useState<string>("");
	const [action, setAction] = useState<string>("");
	const [result, setResult] = useState<string>("");
	const [fromIso, setFromIso] = useState<string>("");
	const [toIso, setToIso] = useState<string>("");

	const filters: AuditLogFilters = useMemo(
		() => ({
			user_id: userId || undefined,
			resource_type: resourceType || undefined,
			action: action || undefined,
			result: (result as AuditResult) || undefined,
			from: fromIsoDate(fromIso, false),
			to: fromIsoDate(toIso, true),
			limit: 500,
		}),
		[userId, resourceType, action, result, fromIso, toIso],
	);

	const listQuery = useLogLevelDataAccessList(filters);
	const exportMutation = useLogLevelDataAccessExport();

	const rows: AuditLogRow[] = listQuery.data?.items ?? [];
	const users = listQuery.data?.users ?? [];
	const resourceTypes = listQuery.data?.resource_types ?? [];
	const actions = listQuery.data?.actions ?? [];

	function resetFilters() {
		setUserId("");
		setResourceType("");
		setAction("");
		setResult("");
		setFromIso("");
		setToIso("");
	}

	async function handleExport() {
		const csv = await exportMutation.mutateAsync(filters);
		const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
		downloadCsv(csv, `log-level-data-access-${stamp}.csv`);
	}

	const filterActive =
		!!(userId || resourceType || action || result || fromIso || toIso);

	return (
		<main
			data-testid="log-level-data-access-page"
			className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-6 py-10"
		>
			<header className="flex flex-col gap-1">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Audit report
				</p>
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
					Log-level data access
				</h1>
				<p className="text-sm text-zinc-600">
					Every access event recorded by the platform: who touched what resource, when, and how the
					request resolved. Use filters to narrow, export CSV for compliance packages.
				</p>
			</header>

			<section
				className={`${CARD} p-4`}
				aria-label="Audit log filters"
				data-testid="log-level-data-access-filters"
			>
				<div className="flex flex-wrap items-end gap-3">
					<label className="flex flex-col gap-1">
						<span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
							User
						</span>
						<select
							data-testid="filter-user"
							className={SELECT}
							value={userId}
							onChange={(e) => setUserId(e.target.value)}
						>
							<option value="">All users</option>
							{users.map((u) => (
								<option key={u.id} value={u.id}>
									{u.email ?? u.id}
								</option>
							))}
						</select>
					</label>

					<label className="flex flex-col gap-1">
						<span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
							Resource type
						</span>
						<select
							data-testid="filter-resource-type"
							className={SELECT}
							value={resourceType}
							onChange={(e) => setResourceType(e.target.value)}
						>
							<option value="">All resources</option>
							{resourceTypes.map((t) => (
								<option key={t} value={t}>
									{t}
								</option>
							))}
						</select>
					</label>

					<label className="flex flex-col gap-1">
						<span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
							Action
						</span>
						<select
							data-testid="filter-action"
							className={SELECT}
							value={action}
							onChange={(e) => setAction(e.target.value)}
						>
							<option value="">All actions</option>
							{actions.map((a) => (
								<option key={a} value={a}>
									{a}
								</option>
							))}
						</select>
					</label>

					<label className="flex flex-col gap-1">
						<span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
							Result
						</span>
						<select
							data-testid="filter-result"
							className={SELECT}
							value={result}
							onChange={(e) => setResult(e.target.value)}
						>
							<option value="">All results</option>
							<option value="allow">allow</option>
							<option value="warn">warn</option>
							<option value="deny">deny</option>
						</select>
					</label>

					<label className="flex flex-col gap-1">
						<span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
							From
						</span>
						<input
							data-testid="filter-from"
							type="date"
							className={INPUT}
							value={fromIso}
							onChange={(e) => setFromIso(e.target.value)}
						/>
					</label>

					<label className="flex flex-col gap-1">
						<span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
							To
						</span>
						<input
							data-testid="filter-to"
							type="date"
							className={INPUT}
							value={toIso}
							onChange={(e) => setToIso(e.target.value)}
						/>
					</label>

					<div className="ml-auto flex gap-2">
						{filterActive ? (
							<button
								type="button"
								onClick={resetFilters}
								className={BTN_SECONDARY}
								data-testid="reset-filters"
							>
								Reset
							</button>
						) : null}
						<button
							type="button"
							onClick={handleExport}
							className={BTN_PRIMARY}
							disabled={exportMutation.isPending || rows.length === 0}
							data-testid="export-csv"
						>
							{exportMutation.isPending ? "Exporting…" : "Export CSV"}
						</button>
					</div>
				</div>
				{exportMutation.isError ? (
					<p className="mt-2 text-xs text-red-600" role="alert">
						{exportMutation.error?.message ?? "Export failed."}
					</p>
				) : null}
			</section>

			<section className={CARD} aria-label="Audit log events">
				{listQuery.isLoading ? (
					<p
						className="p-6 text-sm text-zinc-500"
						data-testid="log-level-data-access-loading"
					>
						Loading access events.
					</p>
				) : listQuery.error ? (
					<p className="p-6 text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : rows.length === 0 ? (
					<p
						className="p-8 text-center text-sm text-zinc-500"
						data-testid="log-level-data-access-empty"
					>
						No access events match the current filters.
					</p>
				) : (
					<div className="overflow-x-auto">
						<div className="flex items-center justify-between border-b border-zinc-100 px-4 py-2">
							<p className="text-xs text-zinc-500">
								Showing {rows.length.toLocaleString()} event
								{rows.length === 1 ? "" : "s"}
								{typeof listQuery.data?.count === "number" &&
								listQuery.data.count > rows.length
									? ` of ${listQuery.data.count.toLocaleString()}`
									: null}
							</p>
							{fromIso || toIso ? (
								<p className="text-xs text-zinc-500 tabular-nums">
									{fromIso || "…"} → {toIso || "…"}
								</p>
							) : null}
						</div>
						<table
							className="w-full border-collapse font-mono"
							data-testid="log-level-data-access-table"
						>
							<thead className="bg-zinc-50">
								<tr>
									<th className={TH}>Timestamp</th>
									<th className={TH}>User</th>
									<th className={TH}>Role</th>
									<th className={TH}>Resource</th>
									<th className={TH}>Resource ID</th>
									<th className={TH}>Action</th>
									<th className={TH}>Result</th>
									<th className={TH}>IP</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100">
								{rows.map((r) => (
									<tr
										key={r.id}
										data-testid={`log-level-data-access-row-${r.id}`}
										className="hover:bg-zinc-50"
									>
										<td className={`${TD} whitespace-nowrap tabular-nums text-zinc-700`}>
											{formatTs(r.ts ?? r.created_at)}
										</td>
										<td className={`${TD} truncate max-w-[220px] text-zinc-900`}>
											{r.user_email ?? r.user_id}
										</td>
										<td className={`${TD} text-zinc-600`}>{r.role ?? "—"}</td>
										<td className={`${TD} text-zinc-700`}>{r.resource_type}</td>
										<td
											className={`${TD} truncate max-w-[200px] text-zinc-500`}
											title={r.resource_id}
										>
											{r.resource_id}
										</td>
										<td className={`${TD} text-zinc-700`}>{r.action}</td>
										<td className={TD}>{resultPill(r.result)}</td>
										<td className={`${TD} tabular-nums text-zinc-500`}>
											{r.ip ?? "—"}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</main>
	);
}

export default LogLevelDataAccessPage;
